import React, { useDeferredValue, useEffect, useState, useMemo } from "react";
import axios from "axios";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Highcharts3D from "highcharts/highcharts-3d";
import { motion } from "framer-motion";
import { Container, Row, Col, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FileText, Home, Building2, Search, Filter, CalendarDays, User, Tag, ShieldCheck } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

// ✅ Safe init (prevents double init in hot reload)
if (typeof Highcharts3D === "function") {
  Highcharts3D(Highcharts);
}

const normalizeSearchValue = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, " ");

const BILL_PREFIXES = new Set(["HB", "SB", "HCR", "SCR", "HJR", "SJR", "HR", "SR"]);

const canonicalizeBillId = (value) => {
  const normalizedValue = String(value || "")
    .toUpperCase()
    .trim()
    .replace(/[^A-Z0-9]+/g, "");
  const match = normalizedValue.match(/^([A-Z]+)(\d+)/);

  if (!match) {
    return normalizedValue;
  }

  const [, prefix, numericPart] = match;
  return `${prefix}${parseInt(numericPart, 10)}`;
};

const parseBillQuery = (value) => {
  const normalizedValue = String(value || "")
    .toUpperCase()
    .trim()
    .replace(/[^A-Z0-9]+/g, "");
  const match = normalizedValue.match(/^([A-Z]+)(\d*)$/);

  if (!match) {
    return null;
  }

  const [, prefix, numericPart] = match;

  if (!BILL_PREFIXES.has(prefix)) {
    return null;
  }

  return {
    prefix,
    number: numericPart ? String(parseInt(numericPart, 10)) : "",
  };
};

const getBillIdParts = (billId) => {
  const match = canonicalizeBillId(billId).match(/^([A-Z]+)(\d+)$/);

  if (!match) {
    return null;
  }

  return {
    prefix: match[1],
    number: match[2],
  };
};

const formatBillIdForDisplay = (billId) => {
  const normalizedBillId = String(billId || "").trim().toUpperCase();
  const match = normalizedBillId.match(/^([A-Z]+)(\d+)/);

  if (!match) {
    return normalizedBillId;
  }

  const [, prefix, numericPart] = match;
  const formattedNumber = String(parseInt(numericPart, 10));
  const prefixMap = {
    HB: "H.B.",
    SB: "S.B.",
    HCR: "H.C.R.",
    SCR: "S.C.R.",
    HJR: "H.J.R.",
    SJR: "S.J.R.",
    HR: "H.R.",
    SR: "S.R.",
  };

  return `${prefixMap[prefix] || prefix} ${formattedNumber}`;
};

const matchesBillSearch = (bill, searchValue) => {
  const normalizedSearch = normalizeSearchValue(searchValue);
  const searchTokens = normalizedSearch.split(" ").filter(Boolean);
  const parsedBillQuery = parseBillQuery(searchValue);
  const canonicalBillId = canonicalizeBillId(bill?.bill_id);
  const billIdMatch = canonicalBillId.match(/^([A-Z]+)(\d+)$/);

  if (parsedBillQuery && billIdMatch) {
    const [, billPrefix, billNumber] = billIdMatch;

    if (parsedBillQuery.number) {
      return (
        billPrefix === parsedBillQuery.prefix &&
        billNumber === parsedBillQuery.number
      );
    }

    return billPrefix === parsedBillQuery.prefix;
  }

  const searchableFields = [
    bill?.title,
    bill?.bill_id,
    formatBillIdForDisplay(bill?.bill_id),
    bill?.sponsor,
    bill?.house,
    bill?.chapter,
    bill?.governor_action,
    bill?.date_passed,
    bill?.effective_date,
  ];
  const normalizedHaystack = normalizeSearchValue(searchableFields.join(" "));
  const condensedHaystack = normalizedHaystack.replace(/\s+/g, "");
  const condensedSearch = normalizedSearch.replace(/\s+/g, "");

  return (
    !searchTokens.length ||
    searchTokens.every((token) => normalizedHaystack.includes(token)) ||
    (condensedSearch && condensedHaystack.includes(condensedSearch))
  );
};

const getBillSuggestionScore = (bill, searchValue) => {
  const normalizedSearch = normalizeSearchValue(searchValue);
  const condensedSearch = normalizedSearch.replace(/\s+/g, "");
  const uppercaseLetterQuery = String(searchValue || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");
  const parsedBillQuery = parseBillQuery(searchValue);
  const billIdParts = getBillIdParts(bill?.bill_id);

  if (!normalizedSearch || !billIdParts) {
    return -1;
  }

  if (parsedBillQuery) {
    if (billIdParts.prefix !== parsedBillQuery.prefix) {
      return -1;
    }

    if (!parsedBillQuery.number) {
      return 1000;
    }

    if (billIdParts.number === parsedBillQuery.number) {
      return 1100;
    }

    return billIdParts.number.startsWith(parsedBillQuery.number) ? 900 : -1;
  }

  const isPrefixOnlyQuery =
    uppercaseLetterQuery &&
    !/\d/.test(searchValue) &&
    [...BILL_PREFIXES].some((prefix) => prefix.startsWith(uppercaseLetterQuery));

  if (isPrefixOnlyQuery) {
    return billIdParts.prefix.startsWith(uppercaseLetterQuery) ? 800 : -1;
  }

  const normalizedDisplayId = normalizeSearchValue(formatBillIdForDisplay(bill?.bill_id));
  const normalizedTitle = normalizeSearchValue(bill?.title);
  const normalizedSponsor = normalizeSearchValue(bill?.sponsor);
  const titleWords = normalizedTitle.split(" ").filter(Boolean);
  const sponsorWords = normalizedSponsor.split(" ").filter(Boolean);

  if (normalizedDisplayId.startsWith(normalizedSearch)) {
    return 700;
  }

  if (normalizedTitle.startsWith(normalizedSearch)) {
    return 600;
  }

  if (titleWords.some((word) => word.startsWith(normalizedSearch))) {
    return 500;
  }

  if (normalizedSponsor.startsWith(normalizedSearch)) {
    return 400;
  }

  if (sponsorWords.some((word) => word.startsWith(normalizedSearch))) {
    return 300;
  }

  if (normalizedSearch.length >= 3 && matchesBillSearch(bill, searchValue)) {
    return normalizedTitle.includes(normalizedSearch) || condensedSearch
      ? 200
      : 100;
  }

  return -1;
};

const UtahLegislatureDashboard = () => {
  const senateColor = "#0B314D";
  const senateDark = "#0E283A";
  const houseColor = "#B89961";
  const houseDark = "#333333";
  const getChamberTheme = (chamber) => {
    if (chamber === "Senate") {
      return {
        color: senateColor,
        bg: "rgba(11, 49, 77, 0.12)",
        border: "rgba(14, 40, 58, 0.24)",
        solidBg: senateDark,
        solidText: "#ffffff",
      };
    }

    if (chamber === "House") {
      return {
        color: houseDark,
        bg: "rgba(184, 153, 97, 0.18)",
        border: "rgba(184, 153, 97, 0.30)",
        solidBg: houseColor,
        solidText: houseDark,
      };
    }

    return {
      color: "#475569",
      bg: "#F8FAFC",
      border: "#E2E8F0",
    };
  };
  const [bills, setBills] = useState([]);
  const [search, setSearch] = useState("");
  const [houseFilter, setHouseFilter] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const deferredSearch = useDeferredValue(search);

  useEffect(() => {
    fetchBills();
  }, []);

  const fetchBills = async () => {
    try {
      setLoading(true);
      const res = await axios.get("https://utsb.utahdata.org/bdxbackend/api/passed-bills");
      setBills(res.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to load bills data");
    } finally {
      setLoading(false);
    }
  };

  // 🔎 FILTER LOGIC (SAFE)
  const filtered = useMemo(() => {
    return bills.filter((b) => {
      return (
        (!houseFilter || b?.house === houseFilter) &&
        matchesBillSearch(b, deferredSearch)
      );
    });
  }, [bills, deferredSearch, houseFilter]);

  const searchSuggestions = useMemo(() => {
    if (!search.trim()) {
      return [];
    }

    return bills
      .map((bill) => ({
        bill,
        score:
          (!houseFilter || bill?.house === houseFilter)
            ? getBillSuggestionScore(bill, search)
            : -1,
      }))
      .filter((item) => item.score >= 0)
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }

        const aBillId = canonicalizeBillId(a.bill?.bill_id);
        const bBillId = canonicalizeBillId(b.bill?.bill_id);
        return aBillId.localeCompare(bBillId, undefined, { numeric: true });
      })
      .map((item) => item.bill)
      .slice(0, 6);
  }, [bills, search, houseFilter]);

  // 📊 STATS (MEMOIZED)
  const { total, house, senate } = useMemo(() => {
    const total = bills.length;
    const house = bills.filter((b) => b.house === "House").length;
    const senate = bills.filter((b) => b.house === "Senate").length;
    return { total, house, senate };
  }, [bills]);

  // 📊 3D CHART CONFIG
  const chartOptions = useMemo(() => ({
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      options3d: {
        enabled: true,
        alpha: 45,
      },
    },
    credits: {
      enabled: false,
    },
    title: { text: "" },
    plotOptions: {
      pie: {
        innerSize: 80,
        depth: 45,
        dataLabels: {
          enabled: true,
          style: {
            color: "#000",
            fontWeight: "bold",
          },
        },
      },
    },
    series: [
      {
        name: "Bills",
        data: [
          { name: "House", y: house, color: houseColor },
          { name: "Senate", y: senate, color: senateColor },
        ],
      },
    ],
  }), [house, senate, houseColor, senateColor]);

  const shouldShowSuggestions =
    isSearchFocused && search.trim() && searchSuggestions.length > 0;

  const handleSuggestionSelect = (bill) => {
    setSearch(formatBillIdForDisplay(bill?.bill_id));
    setIsSearchFocused(false);
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        color: "#333",
        backgroundColor: "#f8fafc"
      }}
    >
      <Header />

      <div
        className="flex-grow"
        style={{
          background:
            "radial-gradient(1200px 640px at 15% 0%, rgba(153, 27, 27, 0.08), transparent 60%), radial-gradient(900px 560px at 90% 10%, rgba(31, 41, 55, 0.06), transparent 55%), #f8fafc",
        }}
      >
        {/* --- HERO SECTION --- */}
        <div
          className="flex items-center"
          style={{
            position: "relative",
            minHeight: "100vh",
            background: `linear-gradient(135deg, rgba(11, 18, 32, 0.65) 0%, rgba(30, 41, 59, 0.66) 100%), url(/img/landing-webp/Utah-legislature.webp)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            color: "white",
            padding: "7rem 0 6rem 0",
            clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
            marginBottom: "2rem",
          }}
        >
          {/* soft glow accents */}
          {/* <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background:
                "radial-gradient(600px 260px at 20% 30%, rgba(220, 38, 38, 0.3), transparent 60%), radial-gradient(600px 260px at 80% 25%, rgba(156, 163, 175, 0.25), transparent 60%)",
              mixBlendMode: "screen",
            }}
          /> */}

          <Container className="relative z-10 py-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Row className="align-items-center text-center">
                <Col lg={10} className="mx-auto">
                  <Badge
                    bg="danger"
                    className="mb-3 px-3 py-2 text-uppercase fw-bold rounded-pill"
                    style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}
                  >
                    Bills Passed in 2026 Session
                  </Badge>
                  <h1
                    className="display-4 fw-bold mb-3 tracking-tight"
                    style={{
                      color: "#ffffff",
                      textShadow: "0 10px 34px rgba(0,0,0,0.55)",
                    }}
                  >
                    Utah Legislature 2026 
                  </h1>
                  <div className="mb-3 inline-flex flex-col items-center rounded-2xl border border-white/20 bg-white/10 px-5 py-3 text-white/90 shadow-lg backdrop-blur-sm">
                    <p className="mb-1 text-xs font-semibold uppercase tracking-[0.24em] text-white/70">
                      2026 General Session of the 66th Legislature
                    </p>
                    <p className="mb-0 text-sm font-medium text-white">
                      January 20, 2026 to March 6, 2026
                    </p>
                  </div>
                  <p className="lead opacity-90 mb-4 px-lg-5 fw-light text-white">
                    See the details of bills moving through the Utah State
                    House and Senate, that have been passed in the 2026 session.
                  </p>
                  
                  {/* BREADCRUMBS */}
                  <div className="flex justify-center flex-wrap gap-2 text-sm text-white/80 mt-6 font-medium tracking-wide">
                    <Link to="/" className="hover:text-white transition-colors">
                      Home
                    </Link>
                    <span className="opacity-50">/</span>
                    <Link to="/utah-at-a-glance" className="hover:text-white transition-colors">
                      Utah At A Glance
                    </Link>
                    <span className="opacity-50">/</span>
                    <span className="text-white">Utah Legislature</span>
                  </div>
                  
                </Col>
              </Row>
            </motion.div>
          </Container>
        </div>

        <Container
          style={{ marginTop: "2rem", position: "relative", zIndex: 100 }}
          className="pb-12"
        >

      {/* ⚠️ ERROR */}
      {error && (
        <div
          className="p-3 rounded mb-4"
          style={{ background: "rgba(184, 153, 97, 0.18)", color: houseDark }}
        >
          {error}
        </div>
      )}

      {/* ⏳ LOADING */}
      {loading && (
        <div className="text-center text-gray-600 py-10">
          Loading bills data...
        </div>
      )}

      {!loading && (
        <>
          {/* 📊 KPI CARDS */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[{
              label: "Total Bills Passed",
              // kicker: "Legislative Pulse",
              value: `${total} / 1292`,
              note: `${house} House-origin bills and ${senate} Senate-origin bills`,
              badge: "Overview",
              icon: FileText,
              iconBg: "linear-gradient(135deg, rgba(184,153,97,0.18), rgba(11,49,77,0.14))",
              iconColor: "#0F172A",
              borderColor: "#CBD5E1",
              topAccent: "linear-gradient(90deg, #B89961 0%, #0B314D 100%)",
              badgeBg: "#F8FAFC",
              badgeColor: "#475569",
              shadow: "0 18px 40px rgba(15,23,42,0.10)"
            }, {
              label: "House Bills Passed",
              // kicker: "Blue Chamber",
              value: `${house}`,
              note: "House-origin bills cleared this session",
              badge: "House",
              icon: Home,
              iconBg: "rgba(184,153,97,0.18)",
              iconColor: houseDark,
              borderColor: "rgba(184,153,97,0.30)",
              topAccent: `linear-gradient(90deg, ${houseColor} 0%, ${houseDark} 100%)`,
              badgeBg: "rgba(184,153,97,0.18)",
              badgeColor: houseDark,
              shadow: "0 18px 40px rgba(184,153,97,0.18)"
            }, {
              label: "Senate Bills Passed",
              // kicker: "Red Chamber",
              value: `${senate}`,
              note: "Senate-origin bills cleared this session",
              badge: "Senate",
              icon: Building2,
              iconBg: "rgba(11,49,77,0.12)",
              iconColor: senateColor,
              borderColor: "rgba(14,40,58,0.24)",
              topAccent: `linear-gradient(90deg, ${senateColor} 0%, ${senateDark} 100%)`,
              badgeBg: "rgba(11,49,77,0.12)",
              badgeColor: senateColor,
              shadow: "0 18px 40px rgba(11,49,77,0.16)"
            }].map((card, i) => {
              const Icon = card.icon;
              return (
                <motion.div
                  key={i}
                  whileHover={{ y: -6, scale: 1.01 }}
                  className="relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-lg p-6 border"
                  style={{
                    borderColor: card.borderColor,
                    boxShadow: card.shadow,
                  }}
                >
                  <div
                    className="absolute inset-x-0 top-0 h-1.5"
                    style={{ background: card.topAccent }}
                  />
                  <div
                    className="absolute -right-8 -top-8 h-24 w-24 rounded-full blur-2xl opacity-70"
                    style={{ background: card.iconBg }}
                  />

                  <div className="relative">
                    <div className="flex justify-between items-start mb-5">
                      <div>
                        <p className="m-0 text-[11px] uppercase tracking-[0.28em] text-slate-400">
                          {card.kicker}
                        </p>
                        <h3 className="mt-2 text-lg font-semibold text-slate-900">
                          {card.label}
                        </h3>
                      </div>
                      <div
                        className="rounded-2xl p-3 border"
                        style={{
                          background: card.iconBg,
                          borderColor: card.borderColor,
                        }}
                      >
                        <Icon size={24} style={{ color: card.iconColor }} />
                      </div>
                    </div>

                    <div className="flex items-end justify-between gap-4">
                      <div>
                        <h2 className="m-0 text-4xl font-bold tracking-tight text-slate-950">
                          {card.value}
                        </h2>
                        <p className="mt-3 mb-0 text-sm text-slate-500">
                          {card.note}
                        </p>
                      </div>
                      <div
                        className="shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold"
                        style={{
                          backgroundColor: card.badgeBg,
                          color: card.badgeColor,
                        }}
                      >
                        {card.badge}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* 📊 CHART */}
          <div className="mt-10 bg-white/60 backdrop-blur-lg p-6 rounded-2xl shadow-xl">
            <h2 className="text-xl font-bold mb-4">
              Bills Distribution
            </h2>
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
            />
          </div>

          {/* 🔎 FILTERS */}
          <div className="mt-10 bg-white/80 backdrop-blur-md p-5 rounded-2xl shadow-lg border border-gray-100 flex flex-col md:flex-row gap-4 items-center transition-all">
            <div className="relative w-full md:w-2/3">
              <div
                className="flex items-center bg-white border border-gray-200 rounded-xl shadow-sm transition-all overflow-hidden pl-4 pr-2"
                style={{
                  boxShadow: isSearchFocused ? "0 0 0 2px rgba(11,49,77,0.18)" : undefined,
                  borderColor: isSearchFocused ? senateColor : undefined,
                }}
              >
                <Search className="text-gray-400 shrink-0" size={20} />
                <input
                  placeholder="Search by bill title, sponsor, or ID..."
                  className="w-full py-3 px-3 border-none focus:ring-0 outline-none bg-transparent placeholder-gray-400 text-gray-700 font-medium"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => {
                    window.setTimeout(() => setIsSearchFocused(false), 120);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && searchSuggestions.length > 0) {
                      e.preventDefault();
                      handleSuggestionSelect(searchSuggestions[0]);
                    }
                  }}
                />
              </div>

              {shouldShowSuggestions && (
                <div className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
                  {searchSuggestions.map((bill) => (
                    <button
                      key={bill?.bill_id}
                      type="button"
                      className="flex w-full items-start justify-between gap-4 border-b border-gray-100 px-4 py-3 text-left transition hover:bg-gray-50 last:border-b-0"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSuggestionSelect(bill)}
                    >
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900">
                          {formatBillIdForDisplay(bill?.bill_id)}
                        </div>
                        <div className="truncate text-sm text-gray-500">
                          {bill?.title}
                        </div>
                      </div>
                      <div
                        className="shrink-0 rounded-full px-2 py-1 text-xs font-medium"
                        style={{
                          backgroundColor: getChamberTheme(bill?.house).bg,
                          color: getChamberTheme(bill?.house).color,
                        }}
                      >
                        {bill?.house}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div
              className="flex items-center w-full md:w-1/3 bg-white border border-gray-200 rounded-xl shadow-sm transition-all overflow-hidden pl-4 pr-2 relative"
              style={{
                boxShadow: houseFilter ? "0 0 0 2px rgba(184,153,97,0.18)" : undefined,
                borderColor: houseFilter ? houseColor : undefined,
              }}
            >
              <Filter className="text-gray-400 shrink-0" size={20} />
              <select
                className="w-full py-3 px-3 border-none focus:ring-0 outline-none bg-transparent appearance-none text-gray-700 font-medium cursor-pointer"
                value={houseFilter}
                onChange={(e) => setHouseFilter(e.target.value)}
              >
                <option value="">All Origin Houses</option>
                <option value="House">House</option>
                <option value="Senate">Senate</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center px-1 text-gray-500">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
              </div>
            </div>
          </div>

          {/* 📄 BILL LIST */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">

            {filtered.length === 0 && (
              <div className="col-span-full text-center text-gray-500">
                No bills found
              </div>
            )}

            {filtered.map((bill, i) => {
              const chamberTheme = getChamberTheme(bill?.house);

              return (
              <motion.div
                key={i}
                whileHover={{ y: -5 }}
                className="bg-white p-5 rounded-2xl shadow-lg border hover:shadow-2xl transition flex flex-col"
                style={{ borderColor: chamberTheme.border }}
              >
                <div className="flex justify-between items-start mb-2">
                  <span
                    className="font-bold text-lg"
                    style={{ color: chamberTheme.color }}
                    title={bill?.bill_id}
                  >
                    {formatBillIdForDisplay(bill?.bill_id)}
                  </span>
                  <span className="text-xs font-semibold bg-green-100 text-green-800 px-2 py-1 rounded-full whitespace-nowrap">
                    Passed
                  </span>
                </div>

                <h3 className="font-semibold text-gray-900 mb-3 line-clamp-2" title={bill?.title}>
                  {bill?.title}
                </h3>

                <div className="flex-grow space-y-3 text-sm text-gray-600 mt-2">
                  {bill?.chief_sponsor && (
                    <div className="flex items-center justify-between border-b border-gray-50 pb-2">
                      <span className="font-medium flex items-center gap-2 text-gray-500"><User size={14} /> Bill Sponsor</span>
                      <span className="text-gray-800 font-medium">{bill.chief_sponsor}</span>
                    </div>
                  )}
                   {bill?.floor_sponsor && (
                    <div className="flex items-center justify-between border-b border-gray-50 pb-2">
                      <span className="font-medium flex items-center gap-2 text-gray-500"><User size={14} /> Floor Sponsor</span>
                      <span className="text-gray-800 font-medium">{bill.floor_sponsor}</span>
                    </div>
                  )}
                  {bill?.house && (
                    <div className="flex items-center justify-between border-b border-gray-50 pb-2">
                      <span className="font-medium flex items-center gap-2 text-gray-500">
                        <Building2 size={14} style={{ color: chamberTheme.color }} />
                        Origin House
                      </span>
                      <span
                        className="font-medium px-2 py-1 rounded-full text-xs"
                        style={{
                          color: chamberTheme.solidText,
                          backgroundColor: chamberTheme.solidBg,
                        }}
                      >
                        {bill.house}
                      </span>
                    </div>
                  )}
                  {bill?.date_passed && (
                    <div className="flex items-center justify-between border-b border-gray-50 pb-2">
                      <span className="font-medium flex items-center gap-2 text-gray-500"><CalendarDays size={14} /> Date Passed</span>
                      <span className="text-gray-800 font-medium">{bill.date_passed}</span>
                    </div>
                  )}
                  {bill?.effective_date && (
                    <div className="flex items-center justify-between border-b border-gray-50 pb-2">
                      <span className="font-medium flex items-center gap-2 text-gray-500"><CalendarDays size={14} /> Effective Date</span>
                      <span className="text-gray-800 font-medium">{bill.effective_date}</span>
                    </div>
                  )}
                  {bill?.governor_action && (
                    <div className="flex items-center justify-between border-b border-gray-50 pb-2">
                      <span className="font-medium flex items-center gap-2 text-gray-500"><ShieldCheck size={14} /> Gov. Action</span>
                      <span className="text-gray-800 font-medium text-right leading-tight max-w-[50%]">{bill.governor_action} {bill?.governor_action_date && <span className="block text-xs text-gray-400">({bill.governor_action_date})</span>}</span>
                    </div>
                  )}
                  {bill?.chapter && (
                    <div className="flex items-center justify-between border-b border-gray-50 pb-2">
                      <span className="font-medium flex items-center gap-2 text-gray-500"><Tag size={14} /> Chapter</span>
                      <span className="text-gray-800 font-medium">{bill.chapter}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100">
                  {bill?.link ? (
                    <a
                      href={bill.link}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:underline font-medium text-sm inline-flex items-center"
                      style={{ color: senateColor }}
                    >
                      View Details <span className="ml-1">→</span>
                    </a>
                  ) : (
                    <span className="text-gray-400 text-sm">No details link available</span>
                  )}
                </div>
              </motion.div>
            );
            })}
          </div>
        </>
      )}

        </Container>
      </div>

      <div className="px-4 pb-10 sm:px-6 lg:px-8">
        <Container>
          <div className="rounded-2xl border border-slate-200 bg-white/80 p-5 shadow-lg backdrop-blur-md">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">
              Data Source
            </p>
            <a
              href="https://le.utah.gov"
              target="_blank"
              rel="noreferrer"
              className="text-base font-medium underline underline-offset-4 transition"
              style={{ color: senateColor, textDecorationColor: `${senateColor}66` }}
            >
              Utah State Legislature
            </a>
          </div>
        </Container>
      </div>

      <Footer />
    </div>
  );
};

export default UtahLegislatureDashboard;
